import { ButtonElement } from "appworks/graphics/elements/button-element";
import { Layers } from "appworks/graphics/layers/layers";
import { Services } from "appworks/services/services";
import { ControlFactory } from "appworks/ui/factories/control-factory";
import { GenericControlFactory } from "appworks/ui/factories/generic-control-factory";
import { GetStandardFactories } from "appworks/ui/factories/get-standard-factories";
import { UIFlagState } from "appworks/ui/flags/ui-flag-state";
import { UIFlag, UIMenuFlag } from "appworks/ui/flags/ui-flags";
import { UI, UIScene } from "appworks/ui/ui";
import { deviceInfo } from "appworks/utils/device-info";
import { GetEpicAutoplayFactories } from "epic/controls/factories/get-epic-autoplay-factories";
import { GetEpicStandardFactories } from "epic/controls/factories/get-epic-standard-factories";
import { gamevyModel } from "gamevy/gamevy-model";
import { GamevyCustomLobbyControl } from "gamevy/ui/controls/gamevy-custom-lobby-control";
import Logger = require("js-logger");
import { slotModel } from "slotworks/model/slot-model";
import { Jurisdiction } from "slotworks/services/jurisdiction/jurisdiction-service";

export function EpicSetupUI(...factories: ControlFactory[]) {
    setupSpin();
    setupTurbo();
    setupSkip();
    setupBetBar();
    setupAutoplay();
    setupRules();
    setupMenus();
    setupPaytable();
    setupHeader();
    setupGermany();

    UI.init([
        ...GetStandardFactories(),
        ...GetEpicAutoplayFactories(),
        ...GetEpicStandardFactories(),
        new GenericControlFactory("home", ButtonElement, GamevyCustomLobbyControl),
        ...factories
    ]);
}

function setupSpin() {
    const smartButtonUIScenes: UIScene[] = [
        { scene: "minimal", show: new UIFlagState(UIFlag.BONUS) },
        { scene: "freespins", show: UIFlagState.FREESPINS },
        { scene: "auto", show: new UIFlagState(UIFlag.AUTO_SPIN), enable: new UIFlagState(UIFlag.ALWAYS, UIFlag.NO_SKIP | UIFlag.MIN_SPIN_COOLDOWN) }
    ];

    let spinButtonFlagState: UIFlagState;

    if (deviceInfo.isDesktop) {
        spinButtonFlagState = new UIFlagState(UIFlag.ALWAYS);
    } else {
        spinButtonFlagState = new UIFlagState(UIFlag.ALWAYS, UIMenuFlag.PAYTABLE);
    }

    smartButtonUIScenes.push({ scene: "spin", show: spinButtonFlagState, enable: new UIFlagState(UIFlag.ALWAYS, UIFlag.NO_SKIP | UIFlag.MIN_SPIN_COOLDOWN) });

    UI.layer(Layers.get("SmartButton"), smartButtonUIScenes);
}

function setupTurbo() {
    const turboAllowed = slotModel.read().regulations.turboAllowed;
    if (!turboAllowed) {
        return;
    }

    const turboUIScenes: UIScene[] = [
        { scene: "minimal", show: new UIFlagState(UIFlag.BONUS) },
        { scene: "freespins", show: UIFlagState.FREESPINS },
        { scene: "auto", show: new UIFlagState(UIFlag.AUTO_SPIN), enable: new UIFlagState(UIFlag.ALWAYS, UIFlag.NO_SKIP | UIFlag.MIN_SPIN_COOLDOWN) }
    ];

    let turboFlagState: UIFlagState;

    if (deviceInfo.isDesktop) {
        turboFlagState = new UIFlagState(UIFlag.ALWAYS);
    } else {
        turboFlagState = new UIFlagState(UIFlag.ALWAYS, UIMenuFlag.PAYTABLE);
    }

    turboUIScenes.push({ scene: "spin", show: turboFlagState, enable: new UIFlagState(UIFlag.ALWAYS, UIFlag.NO_SKIP | UIFlag.MIN_SPIN_COOLDOWN) });

    UI.layer(Layers.get("Turbo"), turboUIScenes);
}

function setupSkip() {
    const skipAllowed = slotModel.read().regulations.skipAllowed;
    if (!skipAllowed) {
        return;
    }

    UI.layer(Layers.get("Skip"), [{
        scene: "skip",
        show: new UIFlagState(UIFlag.GAME_IN_PROGRESS, UIFlag.IDLE),
        enable: new UIFlagState(UIFlag.ALWAYS, UIFlag.NO_SKIP | UIFlag.MIN_SPIN_COOLDOWN)
    }]);
}

function setupBetBar() {
    UI.layer(Layers.get("BetBar"), [
        { scene: "minimal", show: new UIFlagState(UIFlag.BONUS) },
        { scene: "freespins", show: UIFlagState.FREESPINS, enable: UIFlagState.NEVER },
        { scene: "base", show: UIFlagState.ALWAYS, enable: UIFlagState.IDLE }
    ]);
}

function setupAutoplay() {
    const juriAllowsAutoplay = Services.jurisdiction.getAutoplayEnabled();
    if (!juriAllowsAutoplay) {
        return;
    }

    UI.toggle(Layers.get("Autoplay"), "ui_autoplay", UIMenuFlag.AUTO);
    UI.button(Layers.get("MenuContent"), "close", 0, [UIMenuFlag.AUTO]);

    let autoFlagState: UIFlagState;

    if (deviceInfo.isDesktop) {
        autoFlagState = new UIFlagState(UIFlag.ALWAYS, UIFlag.FREE_SPINS | UIFlag.BONUS);
    } else {
        autoFlagState = new UIFlagState(UIFlag.ALWAYS, UIMenuFlag.PAYTABLE | UIFlag.FREE_SPINS | UIFlag.BONUS);
    }

    UI.layer(Layers.get("Autoplay"), [
        { scene: "minimal", show: new UIFlagState(UIFlag.BONUS), enable: UIFlagState.NOT_FREE_BETS },
        { scene: "auto", show: new UIFlagState(UIFlag.AUTO_SPIN, UIFlag.FREE_SPINS), enable: UIFlagState.NOT_FREE_BETS },
        { scene: "spin", show: autoFlagState, enable: new UIFlagState(UIFlag.IDLE, UIFlag.FREE_BETS) }
    ]);
}

function setupRules() {
    UI.toggle(Layers.get("BetBar"), "ui_settings", UIMenuFlag.RULES);

    UI.button(Layers.get("MenuContent"), "close", 0, [UIMenuFlag.RULES]);
}

function setupMenus() {
    UI.layer(Layers.get("MenuContent"), [
        { scene: "autoplay", show: UIFlagState.AUTOPLAY_MENU },
        { scene: "rules", show: UIFlagState.RULES_MENU },
        { scene: "yggdrasil", show: UIFlagState.NEVER }
    ]);
}

function setupPaytable() {
    UI.toggle(Layers.get("BetBar"), "ui_paytable", UIMenuFlag.PAYTABLE);
    if (Layers.get("PaytableCommon")) {
        UI.button(Layers.get("PaytableCommon"), "close", 0, [UIMenuFlag.PAYTABLE]);
        UI.button(Layers.get("PaytableCommon"), "close_portrait", 0, [UIMenuFlag.PAYTABLE]);
        UI.layer(Layers.get("PaytableCommon"), [{ scene: "paytable", show: new UIFlagState(UIMenuFlag.PAYTABLE) }]);
    } else {
        Logger.error("PaytableCommon layer is missing. This should be added to the game for paytable support in the Epic UI.");
    }
}

function setupHeader() {
    UI.layer(Layers.get("Header"), [{ scene: "header", show: UIFlagState.ALWAYS }]);
}

function setupGermany() {
    if (Layers.get("Jurisdiction")) {
        const enabled = gamevyModel.jurisdiction === Jurisdiction.Germany ? UIFlag.ALWAYS : UIFlag.NEVER;
        const disabled = UIFlag.BONUS | UIFlag.FREE_SPINS | UIMenuFlag.MENU;
        UI.layer(Layers.get("Jurisdiction"), [{ scene: "base", show: new UIFlagState(enabled, disabled) }]);
    } else {
        throw new Error("Games need German jurisidictional info psd layers");
    }
}
